/* istanbul ignore file */
import React from 'react';
import type { FeatureFlags } from '@noths/polaris-client-feature-flags';
import { getFeatureFlags } from '@noths/polaris-client-feature-flags';
import { CookieModal } from '@noths/polaris-client-gdpr-compliance';
import { GTMWithDataLayer } from '@noths/polaris-client-google-analytics';
import { setLocaleCookies } from '@noths/polaris-client-localisation';
import { ErrorBoundary, getLayout, LayoutTypes } from '@noths/polaris-client-next-components';
import {
  FontFaces as LegacyFontFaces,
  Palette as LegacyPalette,
} from '@noths/polaris-client-ribbons-base';
import type { SiteChromeContent } from '@noths/polaris-client-site-chrome-ui';
import { GlobalStyles } from '@noths/polaris-design-system-theme-brand2026customer';
import type { AppContextPlusCookies } from '@noths/polaris-dev-ts-types';
import type { AppProps } from 'next/app';
import NextApp from 'next/app';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import { fetchGlobalContent } from 'src/services/contentstack';
import { IN_PROD } from 'src/settings';
import type { NextConfig } from 'src/types/nextConfig';
import { getServerSidePageEventObject } from 'src/utils/gaPageEvent';

const BrowserLogs = dynamic(() =>
  import(
    /* webpackChunkName: "datadog_browser_logs" */ '@noths/polaris-client-next-components/src/monitoring/BrowserLogs'
  ).then((mod) => mod.BrowserLogs),
);

const config = getConfig() as NextConfig;
const { appVersion, version } = config.publicRuntimeConfig;
const { publicRuntimeConfig } = config;
const { ENABLE_LOGGING, ENV_TYPE, NODE_ENV, monitoring } = publicRuntimeConfig;

interface CustomAppProps extends AppProps {
  featureFlags?: FeatureFlags;
  siteChromeContent?: SiteChromeContent | null;
}

const App = ({ Component, featureFlags = {}, pageProps, siteChromeContent }: CustomAppProps) => {
  const pageEventData = getServerSidePageEventObject(version, appVersion);
  const errorBoundaryProps = {
    env: NODE_ENV,
    isLoggingEnabled: ENABLE_LOGGING,
  };

  const layoutType = LayoutTypes.CLIENT_SITE_CHROME_UI;
  const LayoutComponent = getLayout(layoutType);
  const enableBrowserLogging = monitoring?.applicationId && ENV_TYPE === 'production';
  const isProdLikeEnv = ENV_TYPE === 'production' || ENV_TYPE === 'staging';

  return (
    <>
      <Head>
        <title>Apply to sell with us</title>
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <CookieModal useTestScript={!IN_PROD} />
      </Head>
      <GTMWithDataLayer pageEventData={pageEventData as Record<string, unknown>} />
      {enableBrowserLogging && (
        <BrowserLogs
          clientToken={monitoring?.clientToken}
          forwardConsoleLogs={monitoring.forwardConsoleLogs}
          logsLoggerLevel={monitoring?.logsLoggerLevel}
          logsSampleRate={monitoring?.logsSampleRate}
          monitoringEnv={monitoring?.monitoringEnv}
          serviceName={monitoring?.serviceName}
          version={version}
        />
      )}
      <LegacyFontFaces />
      <LegacyPalette />
      <GlobalStyles />
      <LayoutComponent
        NODE_ENV={NODE_ENV}
        featureFlags={featureFlags}
        isProdLikeEnv={isProdLikeEnv}
        siteChromeContent={siteChromeContent ?? undefined}
      >
        <ErrorBoundary {...errorBoundaryProps}>
          <Component {...pageProps} />
        </ErrorBoundary>
      </LayoutComponent>
    </>
  );
};

App.getInitialProps = async (
  appContext: AppContextPlusCookies,
): Promise<{
  featureFlags?: FeatureFlags;
  pageProps: Record<string, unknown>;
  siteChromeContent?: SiteChromeContent | null;
}> => {
  const {
    ctx: { req, res },
  } = appContext;

  if (req && res) {
    setLocaleCookies({ req, res });
  }

  const featureFlags = (req && getFeatureFlags(req)) || {};
  const appProps = await NextApp.getInitialProps(appContext);
  const [siteChromeContent] = await fetchGlobalContent({ featureFlags });

  return {
    pageProps: appProps.pageProps,
    siteChromeContent,
    featureFlags,
  };
};

export default App;
